import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, Palette, Plus, X } from "lucide-react";
import { toast } from "sonner";

const FONTS = ["Outfit", "Playfair Display", "Space Grotesk", "IBM Plex Serif", "Cormorant Garamond", "Merriweather", "Manrope"];

const TEMPLATES = [
  { key: "classic", label: "Klasik", desc: "Formal dengan garis tebal & tabel bordered." },
  { key: "minimal", label: "Minimalis", desc: "Angka besar, banyak white space." },
  { key: "modern", label: "Modern", desc: "Header banner warna primer + card total." },
  { key: "corporate", label: "Korporat", desc: "Sidebar strip logo vertikal untuk BUMN/enterprise." },
  { key: "editorial", label: "Editorial", desc: "Gaya majalah, tipografi serif besar terpusat." },
  { key: "compact", label: "Kompak", desc: "Info padat multi-kolom untuk invoice detail banyak." },
  { key: "dark", label: "Gelap", desc: "Background gelap kontras tinggi, kesan premium tech." },
  { key: "blueprint", label: "Blueprint", desc: "Kertas grid teknis dengan bingkai dashed." },
];

export default function Settings() {
  const { brand, reload } = useBrand();
  const [form, setForm] = useState({
    brand_name: "", tagline: "", invoice_footer: "", logo_file_id: null,
    primary_color: "#2D4739", sidebar_color: "#151A18", accent_color: "#D99C3B",
    invoice_font: "Outfit", invoice_template: "classic", bank_accounts: [],
    signature_file_id: null, signatory_name: "", signatory_title: "",
    company_address: "", company_npwp: "",
  });
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => {
      setForm({
        brand_name: r.data.brand_name || "Adika Cipta Kreasindo",
        tagline: r.data.tagline || "Finance · Project",
        invoice_footer: r.data.invoice_footer || "",
        logo_file_id: r.data.logo_file_id || null,
        primary_color: r.data.primary_color || "#2D4739",
        sidebar_color: r.data.sidebar_color || "#151A18",
        accent_color: r.data.accent_color || "#D99C3B",
        invoice_font: r.data.invoice_font || "Outfit",
        invoice_template: r.data.invoice_template || "classic",
        signature_file_id: r.data.signature_file_id || null,
        signatory_name: r.data.signatory_name || "",
        signatory_title: r.data.signatory_title || "",
        company_address: r.data.company_address || "",
        company_npwp: r.data.company_npwp || "",
        bank_accounts: r.data.bank_accounts || [],
      });
      if (r.data.logo_file_id) {
        const token = localStorage.getItem("token");
        setPreview(`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.data.logo_file_id}?auth=${token}`);
      }
    });
  }, []);

  // Load selected font for preview
  useEffect(() => {
    if (!form.invoice_font || form.invoice_font === "Outfit") return;
    const id = "preview-font-" + form.invoice_font.replace(/\s+/g,"-");
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id; l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${form.invoice_font.replace(/\s+/g,"+")}:wght@400;500;700&display=swap`;
    document.head.appendChild(l);
  }, [form.invoice_font]);

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, logo_file_id: data.id }));
      const token = localStorage.getItem("token");
      setPreview(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.id}?auth=${token}`);
      toast.success("Logo terupload");
    } catch { toast.error("Upload gagal"); } finally { setUploading(false); }
  };

  const removeLogo = () => { setForm(f => ({ ...f, logo_file_id: null })); setPreview(null); };

  const addBank = () => setForm(f => ({ ...f, bank_accounts: [...f.bank_accounts, { bank_name: "", account_number: "", account_name: "" }] }));
  const setBank = (i, field, val) => setForm(f => {
    const arr = [...f.bank_accounts]; arr[i] = { ...arr[i], [field]: val }; return { ...f, bank_accounts: arr };
  });
  const rmBank = (i) => setForm(f => ({ ...f, bank_accounts: f.bank_accounts.filter((_, x) => x !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Pengaturan tersimpan");
    } catch { toast.error("Gagal menyimpan"); } finally { setBusy(false); }
  };

  const ColorInput = ({ label, value, onChange, testid }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 items-center mt-1">
        <input type="color" value={value} onChange={e=>onChange(e.target.value)} className="w-12 h-9 rounded-sm border border-forest-100 cursor-pointer bg-white" data-testid={testid}/>
        <Input value={value} onChange={e=>onChange(e.target.value)} className="rounded-sm font-mono text-xs" maxLength={7}/>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tag">Pengaturan</div>
        <h1 className="font-display text-4xl tracking-tight mt-1">Branding Aplikasi</h1>
        <p className="text-sm text-forest-400 mt-2">Sesuaikan logo, warna, font, dan rekening pembayaran sesuai identitas perusahaan Anda.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Identitas */}
          <div className="card-grid p-6 space-y-4">
            <div className="label-tag">Identitas Perusahaan</div>
            <div><Label>Nama Merek</Label><Input data-testid="inp-brand-name" value={form.brand_name} onChange={e=>setForm({...form, brand_name:e.target.value})} className="rounded-sm"/></div>
            <div><Label>Tagline</Label><Input data-testid="inp-tagline" value={form.tagline} onChange={e=>setForm({...form, tagline:e.target.value})} className="rounded-sm"/></div>
            <div><Label>Alamat Perusahaan</Label><Textarea data-testid="inp-company-address" value={form.company_address} onChange={e=>setForm({...form, company_address:e.target.value})} className="rounded-sm" rows={3} placeholder="Jl. Contoh No. 123, RT/RW 04/05, Kel. Sample, Kec. Demo, Jakarta Selatan 12345"/></div>
            <div><Label>Nomor NPWP</Label><Input data-testid="inp-company-npwp" value={form.company_npwp} onChange={e=>setForm({...form, company_npwp:e.target.value})} className="rounded-sm font-mono" placeholder="00.000.000.0-000.000"/></div>
            <div>
              <Label>Logo</Label>
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-2 border border-forest-100 px-4 py-2 rounded-sm cursor-pointer text-sm bg-white hover:bg-forest-50">
                  <Upload size={14}/> {uploading?"Mengupload…":"Pilih File"}
                  <input type="file" accept="image/*" hidden data-testid="inp-logo-file" onChange={e=>e.target.files[0]&&uploadLogo(e.target.files[0])}/>
                </label>
                {form.logo_file_id && <button onClick={removeLogo} className="text-[#C45A3D] p-2 hover:bg-forest-50 rounded-sm"><Trash2 size={16}/></button>}
              </div>
            </div>
          </div>

          {/* Warna */}
          <div className="card-grid p-6 space-y-4">
            <div className="label-tag flex items-center gap-2"><Palette size={12}/> Palet Warna</div>
            <div className="grid grid-cols-3 gap-3">
              <ColorInput label="Primer (Tombol)" value={form.primary_color} onChange={(v)=>setForm({...form, primary_color:v})} testid="color-primary"/>
              <ColorInput label="Sidebar" value={form.sidebar_color} onChange={(v)=>setForm({...form, sidebar_color:v})} testid="color-sidebar"/>
              <ColorInput label="Aksen (Logo)" value={form.accent_color} onChange={(v)=>setForm({...form, accent_color:v})} testid="color-accent"/>
            </div>
            <div className="flex gap-2 text-xs">
              <button onClick={()=>setForm({...form, primary_color:"#2D4739", sidebar_color:"#151A18", accent_color:"#D99C3B"})} className="text-forest-400 hover:text-[#151A18] underline">Reset default (hutan)</button>
              <button onClick={()=>setForm({...form, primary_color:"#1E40AF", sidebar_color:"#0F172A", accent_color:"#F59E0B"})} className="text-forest-400 hover:text-[#151A18] underline">Biru korporat</button>
              <button onClick={()=>setForm({...form, primary_color:"#7C2D12", sidebar_color:"#1C1917", accent_color:"#EAB308"})} className="text-forest-400 hover:text-[#151A18] underline">Coklat hangat</button>
            </div>
          </div>

          {/* Font Invoice */}
          <div className="card-grid p-6 space-y-3">
            <div className="label-tag">Font Invoice Cetak</div>
            <Select value={form.invoice_font} onValueChange={(v)=>setForm({...form, invoice_font:v})}>
              <SelectTrigger data-testid="sel-font"><SelectValue/></SelectTrigger>
              <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <div className="p-4 border border-forest-100 rounded-sm bg-white">
              <div style={{ fontFamily: form.invoice_font }} className="text-3xl tracking-tight">INVOICE</div>
              <div style={{ fontFamily: form.invoice_font }} className="text-sm mt-1 text-forest-400">INV-2026-0001 · 28 Agustus 2026</div>
            </div>
          </div>

          {/* Template Invoice */}
          <div className="card-grid p-6 space-y-3">
            <div className="label-tag">Template Layout Invoice</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TEMPLATES.map(t => (
                <button key={t.key} data-testid={`tpl-${t.key}`} onClick={()=>setForm({...form, invoice_template:t.key})}
                  className={`text-left p-4 rounded-sm border-2 transition-colors ${form.invoice_template===t.key ? "border-[#2D4739] bg-forest-50" : "border-forest-100 hover:border-forest-400"}`}>
                  <div className="mb-2 h-20 bg-white border border-forest-100 rounded-sm relative overflow-hidden">
                    {t.key==="classic" && <>
                      <div className="absolute top-2 left-2 w-4 h-4" style={{ backgroundColor: form.accent_color }}/>
                      <div className="absolute top-2 right-2 text-[6px] font-bold">INVOICE</div>
                      <div className="absolute top-8 inset-x-2 h-[1px]" style={{ backgroundColor: form.primary_color }}/>
                      <div className="absolute bottom-2 inset-x-2 space-y-[2px]">
                        <div className="h-[2px] w-full bg-forest-100"/><div className="h-[2px] w-3/4 bg-forest-100"/><div className="h-[2px] w-5/6 bg-forest-100"/>
                      </div>
                    </>}
                    {t.key==="minimal" && <>
                      <div className="absolute top-2 left-2 text-[5px] uppercase tracking-widest text-forest-400">Invoice</div>
                      <div className="absolute top-6 left-2 text-[10px] font-bold" style={{ color: form.primary_color }}>Rp 1.100.000</div>
                      <div className="absolute bottom-2 inset-x-2 space-y-[2px]">
                        <div className="h-[2px] w-full bg-forest-100"/><div className="h-[2px] w-2/3 bg-forest-100"/>
                      </div>
                    </>}
                    {t.key==="modern" && <>
                      <div className="absolute inset-x-0 top-0 h-8 flex items-center px-2" style={{ backgroundColor: form.primary_color }}>
                        <div className="w-3 h-3" style={{ backgroundColor: form.accent_color }}/>
                        <div className="text-[6px] text-white ml-auto">INV-0001</div>
                      </div>
                      <div className="absolute bottom-2 inset-x-2 space-y-[2px]">
                        <div className="h-[2px] w-full bg-forest-100"/><div className="h-[2px] w-3/4 bg-forest-100"/>
                      </div>
                    </>}
                    {t.key==="corporate" && <>
                      <div className="absolute inset-y-0 left-0 w-4" style={{ backgroundColor: form.primary_color }}/>
                      <div className="absolute top-2 left-6 right-2 text-[6px] font-bold" style={{ color: form.primary_color }}>INVOICE</div>
                      <div className="absolute bottom-2 left-6 right-2 space-y-[2px]"><div className="h-[2px] w-full bg-forest-100"/><div className="h-[2px] w-5/6 bg-forest-100"/></div>
                    </>}
                    {t.key==="editorial" && <>
                      <div className="absolute inset-x-2 top-2 h-[2px]" style={{ backgroundColor: form.primary_color }}/>
                      <div className="absolute inset-x-0 top-6 text-center text-[8px] font-bold uppercase tracking-widest" style={{ color: form.primary_color }}>Invoice</div>
                      <div className="absolute bottom-2 inset-x-2 space-y-[2px]"><div className="h-[2px] w-full bg-forest-100"/></div>
                    </>}
                    {t.key==="compact" && <>
                      <div className="absolute top-1 left-1 w-3 h-3" style={{ backgroundColor: form.accent_color }}/>
                      <div className="absolute inset-x-2 top-6 grid grid-cols-4 gap-[1px]">{[0,1,2,3].map(i=><div key={i} className="h-2 bg-forest-100"/>)}</div>
                      <div className="absolute bottom-1 inset-x-1 h-3" style={{ backgroundColor: form.primary_color }}/>
                    </>}
                    {t.key==="dark" && <>
                      <div className="absolute inset-1 rounded-sm" style={{ backgroundColor: "#0F1210" }}/>
                      <div className="absolute top-2 left-2 w-3 h-3" style={{ backgroundColor: form.accent_color }}/>
                      <div className="absolute top-2 right-2 text-[6px]" style={{ color: form.accent_color }}>INV</div>
                      <div className="absolute bottom-2 inset-x-2 space-y-[2px]"><div className="h-[2px] w-full bg-white/20"/><div className="h-[2px] w-2/3 bg-white/20"/></div>
                    </>}
                    {t.key==="blueprint" && <>
                      <div className="absolute inset-1 border-2 border-dashed" style={{ borderColor: form.primary_color }}/>
                      <div className="absolute inset-2 grid grid-cols-4 grid-rows-3 gap-[2px] opacity-30">{Array.from({length:12}).map((_,i)=><div key={i} className="border border-forest-100"/>)}</div>
                    </>}
                  </div>
                  <div className="font-medium text-sm">{t.label}</div>
                  <div className="text-xs text-forest-400 mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Bank Accounts */}
          <div className="card-grid p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-tag">Rekening Pembayaran</div>
              <Button size="sm" onClick={addBank} data-testid="btn-add-bank" className="rounded-sm bg-[#151A18] hover:bg-[#22362B] gap-1 h-8"><Plus size={12}/> Tambah</Button>
            </div>
            {form.bank_accounts.length===0 && <div className="text-sm text-forest-400">Belum ada rekening. Tambahkan agar tampil otomatis di invoice dan email reminder.</div>}
            {form.bank_accounts.map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 border border-forest-100 p-3 rounded-sm">
                <div className="col-span-3"><Label className="text-[10px]">Bank</Label><Input value={b.bank_name} onChange={e=>setBank(i,"bank_name",e.target.value)} placeholder="BCA"/></div>
                <div className="col-span-4"><Label className="text-[10px]">No. Rekening</Label><Input value={b.account_number} onChange={e=>setBank(i,"account_number",e.target.value)} placeholder="1234567890"/></div>
                <div className="col-span-4"><Label className="text-[10px]">Atas Nama</Label><Input value={b.account_name} onChange={e=>setBank(i,"account_name",e.target.value)} placeholder="PT Contoh Sukses"/></div>
                <div className="col-span-1 flex items-end justify-center"><button onClick={()=>rmBank(i)} className="text-[#C45A3D] p-2 hover:bg-forest-50"><X size={16}/></button></div>
              </div>
            ))}
          </div>

          {/* Signature */}
          <div className="card-grid p-6 space-y-3">
            <div className="label-tag">Tanda Tangan Digital Invoice</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama Penanda Tangan</Label><Input data-testid="inp-signatory-name" value={form.signatory_name} onChange={e=>setForm({...form, signatory_name:e.target.value})} placeholder="Ir. Abqari Adika"/></div>
              <div><Label>Jabatan</Label><Input data-testid="inp-signatory-title" value={form.signatory_title} onChange={e=>setForm({...form, signatory_title:e.target.value})} placeholder="Direktur Utama"/></div>
            </div>
            <div>
              <Label>Gambar Tanda Tangan (PNG transparan disarankan)</Label>
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-2 border border-forest-100 px-4 py-2 rounded-sm cursor-pointer text-sm bg-white hover:bg-forest-50">
                  <Upload size={14}/> Pilih File
                  <input type="file" accept="image/*" hidden data-testid="inp-signature-file" onChange={async e => {
                    if (!e.target.files[0]) return;
                    const fd = new FormData(); fd.append("file", e.target.files[0]);
                    try {
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setForm(f => ({ ...f, signature_file_id: data.id }));
                      toast.success("Tanda tangan terupload");
                    } catch { toast.error("Upload gagal"); }
                  }}/>
                </label>
                {form.signature_file_id && (
                  <>
                    <img alt="signature preview" src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${form.signature_file_id}?auth=${localStorage.getItem("token")}`} className="h-12 object-contain border border-forest-100 rounded-sm px-2 bg-white"/>
                    <button onClick={()=>setForm({...form, signature_file_id:null})} className="text-[#C45A3D] p-2 hover:bg-forest-50 rounded-sm"><Trash2 size={14}/></button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer & Save */}
          <div className="card-grid p-6 space-y-3">
            <div className="label-tag">Catatan Kaki Invoice</div>
            <Textarea data-testid="inp-footer" value={form.invoice_footer} onChange={e=>setForm({...form, invoice_footer:e.target.value})} className="rounded-sm" placeholder="Ketentuan pembayaran, catatan, atau pesan terima kasih" rows={3}/>
            <Button data-testid="btn-save-settings" onClick={save} disabled={busy} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] w-full mt-2">
              {busy ? "Menyimpan…" : "Simpan Semua Pengaturan"}
            </Button>
          </div>
        </div>

        {/* Right: preview */}
        <div className="space-y-4 lg:sticky lg:top-8 lg:self-start">
          <div className="card-grid p-6">
            <div className="label-tag mb-3">Preview Sidebar</div>
            <div style={{ backgroundColor: form.sidebar_color }} className="p-4 rounded-sm flex items-center gap-2">
              {preview ? (
                <img src={preview} alt="logo" className="w-8 h-8 object-contain bg-white/10 rounded-sm"/>
              ) : (
                <div style={{ backgroundColor: form.accent_color }} className="w-8 h-8 flex items-center justify-center font-display font-bold text-[#151A18]">
                  {(form.brand_name || "L").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="text-white">
                <div className="font-display text-base leading-none">{form.brand_name || "Adika Cipta Kreasindo"}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1">{form.tagline || "Finance · Project"}</div>
              </div>
            </div>
            <button style={{ backgroundColor: form.primary_color, color: "white" }} className="mt-3 w-full px-3 py-2 text-sm rounded-sm">Contoh Tombol Primer</button>
          </div>
          <div className="card-grid p-6">
            <div className="label-tag mb-3">Preview Invoice</div>
            <div className="bg-white border border-forest-100 p-4 rounded-sm">
              {preview ? (
                <img src={preview} alt="logo" className="h-12 object-contain"/>
              ) : (
                <div style={{ backgroundColor: form.accent_color }} className="w-12 h-12 flex items-center justify-center font-display font-bold text-[#151A18]">
                  {(form.brand_name || "L").charAt(0).toUpperCase()}
                </div>
              )}
              <div style={{ fontFamily: form.invoice_font }} className="mt-3 text-base">{form.brand_name || "Adika Cipta Kreasindo"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-forest-400 mt-4">Invoice</div>
              <div style={{ fontFamily: form.invoice_font }} className="text-2xl">INV-XXXX</div>
              {form.bank_accounts.length > 0 && (
                <div className="mt-3 pt-3 border-t border-forest-100">
                  <div className="label-tag mb-1">Bayar ke</div>
                  {form.bank_accounts.slice(0,2).map((b,i) => <div key={i} className="text-xs text-forest-400">{b.bank_name} {b.account_number} — {b.account_name}</div>)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
